import type { StaffTeamLoad } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { deepLinks } from "../navigation/deep-links";
import { useAreaHref } from "../navigation/use-offering";
import { AsyncState } from "../ui/async-state";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../ui/cn";
import { Notice } from "../ui/notice";
import { useOptionalHomeRuntime } from "./runtime";
import {
  availableText,
  awayText,
  barPercent,
  customersText,
  namedCustomers,
  noCoverReasons,
  noCoverTitle,
  orderedMembers,
  waitingText,
} from "./team-load";
import { teamLoadKey, useTeamLoad } from "./team-load-queries";

// The side column of Home for a supervisor (CHT-2026): the alert "N customers have no cover" with Assign, and the Team load card. Both read
// one closed answer (`GET /staff/team/load`) and judge nothing: the gaps are the ones of the Coverage area, a bar is a person's customers next
// to the person with the most, and no threshold marks a person as busy. Assign opens the Coverage area where the supervisor already assigns
// cover (one customer: that customer's page; more: the list of the gaps). It adds no assignment rule.

type TeamLoad = StaffTeamLoad;
type NoCover = TeamLoad["noCover"];
type Member = TeamLoad["members"][number];

/** Reads the load again now: the "Refresh" of Home refreshes every card, not only the attention queues. */
export function useRefreshTeamLoad(): () => void {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: teamLoadKey });
}

/** Whether Home draws the column: a runtime that reads the load, and a role that may open Coverage (the supervisor). The API checks again. */
export function useShowsTeamLoad(): boolean {
  const reads = useOptionalHomeRuntime()?.teamLoad !== undefined;
  const coverageHref = useAreaHref()("coverage", deepLinks.coverage());
  return reads && coverageHref !== null;
}

function NoCoverAlert({ noCover }: Readonly<{ noCover: NoCover }>) {
  const areaHref = useAreaHref();
  const [only] = noCover.customers;
  const target =
    noCover.customerCount === 1 && only !== undefined
      ? deepLinks.customerCoverage(only.id)
      : deepLinks.coverage();
  const href = areaHref("coverage", target);
  const reasons = noCoverReasons(noCover);
  return (
    <Notice tone="warning" className="rounded-md px-3.5 py-3">
      <div className="flex w-full items-center justify-between gap-3">
        <div className="grid min-w-0 gap-0.5">
          <p className="m-0 text-sm leading-5 font-semibold">{noCoverTitle(noCover)}</p>
          {reasons === "" ? null : <p className="m-0 text-meta leading-[18px]">{reasons}</p>}
        </div>
        {href === null ? null : (
          <Button
            asChild
            size="sm"
            className="bg-warning text-warning-foreground hover:bg-foreground"
          >
            <Link to={href}>
              Assign<span className="sr-only"> cover to {namedCustomers(noCover)}</span>
            </Link>
          </Button>
        )}
      </div>
    </Notice>
  );
}

/** A bar of the card: the track, and the share of the largest load. Decoration: the numbers are written in the row. */
function Bar({ percent, away }: Readonly<{ percent: number; away: boolean }>) {
  return (
    <svg aria-hidden="true" focusable="false" width="100%" height="8" className="block">
      <rect width="100%" height="8" rx="4" className="fill-border" />
      <rect
        width={`${percent}%`}
        height="8"
        rx="4"
        className={away ? "fill-neutral-dot" : "fill-primary"}
      />
    </svg>
  );
}

function MemberRow({ member, members }: Readonly<{ member: Member; members: readonly Member[] }>) {
  const away = member.awayUntil !== null;
  const notes = [away ? awayText(member.awayUntil as string) : null, waitingText(member)].filter(
    (note) => note !== null,
  );
  return (
    <li className="grid gap-2 py-2 compact:gap-1 compact:py-1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar id={member.staffUserId} name={member.displayName} size="sm" />
          <div className="grid min-w-0">
            <span
              className={cn(
                "truncate text-sm leading-5 font-semibold",
                away && "text-muted-foreground",
              )}
            >
              {member.displayName}
            </span>
            {notes.length === 0 ? null : (
              <span className="text-meta leading-[18px] text-muted-foreground">
                {notes.join(" · ")}
              </span>
            )}
          </div>
        </div>
        <span className="shrink-0 text-meta leading-[18px] font-semibold text-muted-foreground">
          {customersText(member.customers)}
        </span>
      </div>
      <Bar percent={barPercent(member.customers, members)} away={away} />
    </li>
  );
}

function TeamLoadCard({ load }: Readonly<{ load: TeamLoad }>) {
  const members = orderedMembers(load.members);
  return (
    <section aria-labelledby="team-load-title">
      <Card>
        <CardHeader className="flex flex-row items-baseline justify-between gap-3 pb-2 sm:pb-2">
          <CardTitle id="team-load-title">Team load</CardTitle>
          {members.length === 0 ? null : (
            <p className="m-0 text-sm leading-5 font-medium text-muted-foreground">
              {availableText(members)}
            </p>
          )}
        </CardHeader>
        <CardContent className="gap-0">
          {members.length === 0 ? (
            <p className="m-0 text-sm leading-6 text-muted-foreground">
              No active concierge or supervisor to show.
            </p>
          ) : (
            <ul
              className="m-0 grid list-none gap-0 p-0"
              aria-label="Customers and waiting conversations of each person"
            >
              {members.map((member) => (
                <MemberRow key={member.staffUserId} member={member} members={members} />
              ))}
            </ul>
          )}
          {load.membersCapped ? (
            <p className="m-0 pt-2 text-meta leading-[18px] text-muted-foreground">
              More people are on the team than this card lists.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

/** The content of the `side` place of `HomeLayout`. Draw it only when `useShowsTeamLoad()` says so. */
export function TeamLoadSide() {
  const query = useTeamLoad();
  if (query.isPending) {
    return (
      <AsyncState
        kind="loading"
        title="Reading the team load"
        message="Reading who carries which customers."
      />
    );
  }
  if (query.isError) {
    return (
      <AsyncState
        kind="error"
        title="The team load could not be read"
        message="The staff API did not answer. The rest of Home is not affected."
        onRetry={() => void query.refetch()}
      />
    );
  }
  return (
    <>
      {query.data.noCover.customerCount === 0 ? null : (
        <NoCoverAlert noCover={query.data.noCover} />
      )}
      <TeamLoadCard load={query.data} />
    </>
  );
}
